// frontend/hooks/useSidebarLogic.ts

import { useState, useEffect } from "react";
import { useSidebarData } from "./useSidebarData";
import { useSidebarActions } from "./useSidebarActions";

export function useSidebarLogic({ user, router, pathname, setTheme }: any) {
  // 1. 데이터 및 전역 이벤트 훅 호출
  const {
    documents, setDocuments, isError, colorTheme, setColorTheme, fetchDocuments,
    teamRootDocs, personalRootDocs, handoverRootDocs, recentDocs
  } = useSidebarData(user, router, pathname, setTheme);

  // 2. 사이드바 UI 상태 (모달, 컨텍스트 메뉴, 폴더 열림 상태 등)
  const [isSettingsOpen, setIsSettingsOpen] = useState(false);
  const [isTrashOpen, setIsTrashOpen] = useState(false);
  const [expandedDocs, setExpandedDocs] = useState<Record<string, boolean>>({});
  const [contextMenu, setContextMenu] = useState({ visible: false, x: 0, y: 0, docId: null as string | null, title: "", icon: null as string | null, isTrueOwner: false, section: undefined as "recent" | "favorite" | "team" | "personal" | undefined, isFavorite: false, isRoot: false, permLevel: null as "FULL" | "EDIT" | "COMMENT" | "READ" | null, updatedBy: undefined as string | undefined, updatedAt: undefined as string | undefined });
  const [renameModal, setRenameModal] = useState({ visible: false, docId: "", title: "", x: 0, y: 0, icon: null as string | null });
  const [deleteModal, setDeleteModal] = useState({ visible: false, docId: "", title: "" });
  const [createModal, setCreateModal] = useState({ visible: false, type: "PERSONAL" as "TEAM" | "PERSONAL", parentId: null as string | null, parentTitle: "", title: "", templateId: null as string | null }); // templateId 추가

  // 3. API 액션 훅 호출 (상태들을 props로 주입)
  const actions = useSidebarActions({
    user, router, pathname, documents, setDocuments, fetchDocuments, setExpandedDocs,
    createModal, setCreateModal, renameModal, setRenameModal, deleteModal, setDeleteModal,
    contextMenu, setContextMenu
  });

  // 외부 영역 클릭 시 컨텍스트 메뉴 닫기 및 에디터 실시간 수정 낙관적 업데이트(Optimistic UI) 수신
  useEffect(() => {
    const closeContextMenu = () => setContextMenu(prev => ({ ...prev, visible: false }));

    const handleOptimisticUpdate = (e: Event) => {
      const customEvent = e as CustomEvent;
      if (!customEvent.detail) return;

      const { docId, updatedBy, updatedAt } = customEvent.detail;
      // 백엔드 통신 없이, 프론트엔드 메모리 상의 문서 목록 데이터를 즉각 갱신
      setDocuments((prevDocs: any[]) => prevDocs.map(doc =>
        doc.id === docId ? { ...doc, updatedBy, updatedAt } : doc
      ));
    };

    window.addEventListener("click", closeContextMenu);
    window.addEventListener("optimistic-doc-update", handleOptimisticUpdate);

    return () => {
      window.removeEventListener("click", closeContextMenu);
      window.removeEventListener("optimistic-doc-update", handleOptimisticUpdate);
    };
  }, [setDocuments]);

  // UI 상태를 제어하는 간단한 헬퍼 함수들
  const openCreateModal = (type: "TEAM" | "PERSONAL", parentId: string | null, parentTitle: string) => {
    if (user?.role === "GUEST") return alert("외부인은 문서를 생성할 수 없습니다.");
    // 모달을 열 때마다 템플릿 선택을 초기화한다 — 지난 선택이 남아 있으면
    //           빈 문서를 만들려다 엉뚱한 템플릿이 복제된다.
    setCreateModal({ visible: true, type, parentId, parentTitle, title: "", templateId: null });
  };

  const handleContextMenu = (
    e: React.MouseEvent,
    docId: string,
    title: string,
    icon: string | null = null,
    isTrueOwner: boolean = false,
    section?: "recent" | "favorite" | "team" | "personal",
    isFavorite: boolean = false,
    isRoot: boolean = false,
    updatedBy?: string,
    updatedAt?: string,
    permLevel?: "FULL" | "EDIT" | "COMMENT" | "READ" | null,
  ) => {
    e.preventDefault();
    e.stopPropagation();
    setContextMenu({
      visible: true,
      x: e.pageX,
      y: e.pageY,
      docId,
      title,
      icon,
      isTrueOwner,
      section,
      isFavorite,
      isRoot,
      // 문서 소유자는 항상 FULL, 그 외에는 전달된 permLevel, 없으면 READ
      permLevel: isTrueOwner ? "FULL" : (permLevel ?? "READ"),
      updatedBy,
      updatedAt,
    });
  };

  const handleMenuRename = () => {
    if (!contextMenu.docId) return;
    setRenameModal({
      visible: true,
      docId: contextMenu.docId,
      title: contextMenu.title,
      x: contextMenu.x,
      y: contextMenu.y,
      icon: contextMenu.icon,
    });
    setContextMenu(prev => ({ ...prev, visible: false }));
  };

  const handleMenuDelete = () => {
    if (!contextMenu.docId) return;
    setDeleteModal({ visible: true, docId: contextMenu.docId, title: contextMenu.title });
    setContextMenu(prev => ({ ...prev, visible: false }));
  };

  // Sidebar.tsx에서 기존과 완벽히 동일하게 사용할 수 있도록 모든 값과 함수를 반환
  return {
    documents, isError, teamRootDocs, personalRootDocs, handoverRootDocs, recentDocs,
    isSettingsOpen, setIsSettingsOpen, isTrashOpen, setIsTrashOpen, colorTheme, setColorTheme,
    expandedDocs, setExpandedDocs, contextMenu, setContextMenu, renameModal, setRenameModal,
    deleteModal, setDeleteModal, createModal, setCreateModal,
    fetchDocuments, openCreateModal, handleContextMenu, handleMenuRename, handleMenuDelete,
    ...actions // (submitCreateDocument, submitRename, submitDelete, handleMenuDuplicate, handleToggleFavorite, handleRemoveFromRecent, handleCopyLink)
  };
}